import { AlertBus } from '@/common/domain/alert/AlertBus';
import { Authentication } from '@/common/domain/auth/Authentication';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { StafferLanguageRepository } from '@/common/domain/StafferLanguageRepository';

const addTokenBearerTo =
  (config: Partial<AxiosRequestConfig>): ((token: string) => Partial<AxiosRequestConfig>) =>
  token => ({
    ...config,
    headers: {
      Authorization: `Bearer ${token}`,
      ...config.headers,
    },
  });

export class BackendCaller {
  constructor(
    private axiosInstance: AxiosInstance,
    private authentication: Authentication,
    private alertBus: AlertBus,
    private stafferLanguageRepository: StafferLanguageRepository
  ) {}

  async get<T>(uri: string, config: Partial<AxiosRequestConfig> = {}): Promise<AxiosResponse<T>> {
    return await this.axiosInstance.get(uri, await this.configWithHeaders(config)).catch(error => this.throwError(error));
  }

  async post<T>(uri: string, data?: any): Promise<AxiosResponse<T>> {
    return await this.axiosInstance.post(uri, data, await this.configWithHeaders()).catch(error => this.throwError(error));
  }

  async patch<T>(uri: string, data?: any): Promise<AxiosResponse<T>> {
    return await this.axiosInstance.patch(uri, data, await this.configWithHeaders()).catch(error => this.throwError(error));
  }

  async put<T>(uri: string, data: any): Promise<AxiosResponse<T>> {
    return await this.axiosInstance.put(uri, data, await this.configWithHeaders()).catch(error => this.throwError(error));
  }

  private throwError(error: any): never {
    this.alertBus.alert('toasts.errors.backend.unknown', 'danger');
    throw new Error(error);
  }

  private async configWithHeaders(config: Partial<AxiosRequestConfig> = {}): Promise<Partial<AxiosRequestConfig>> {
    config.headers = { 'Accept-Language': this.stafferLanguageRepository.getCurrentLanguage() };

    const token = await this.authentication.jwtToken();

    return token.map(addTokenBearerTo(config)).orElse(config);
  }
}
